import { EventEmitter } from 'events';
import api from './api';
import { getAllTags } from './api';

// 缓存数据类型定义
interface CacheData {
  noteStats: any;
  vipStatus: any;
  aiUsage: any;
  noteUsage: any;
  formatUsage: any;
  tags: any[];
  tagsCount: number;
  lastUpdated: number;
}

// 缓存管理器类
class CacheManager extends EventEmitter {
  private cache: Partial<CacheData> = {};
  private isInitialized = false;
  private isLoading = false;
  private initPromise: Promise<void> | null = null;

  // 缓存有效期：10分钟
  private readonly CACHE_DURATION = 10 * 60 * 1000;

  constructor() {
    super();
    this.setMaxListeners(50); // 增加监听器数量限制
  }

  /**
   * 初始化缓存 - 应用启动时调用
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('CacheManager: 已经初始化，跳过');
      return;
    }

    if (this.initPromise) {
      console.log('CacheManager: 正在初始化中，等待完成');
      return this.initPromise;
    }

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    if (this.isLoading) {
      console.log('CacheManager: 正在加载中，跳过重复初始化');
      return;
    }

    try {
      this.isLoading = true;
      console.log('CacheManager: 开始初始化缓存...');
      const startTime = Date.now();

      // 并行获取所有数据
      const [noteStatsResponse, vipStatusResponse, formatUsageResponse, aiUsageResponse, noteUsageResponse, tagsResponse] = await Promise.all([
        api.get('/notes/stats').catch(err => {
          console.warn('获取笔记统计失败:', err);
          return { data: { totalNotes: 0, todayNotes: 0, totalTags: 0, tagStats: [] } };
        }),
        api.get('/vip/status').catch(err => {
          console.warn('获取VIP状态失败:', err);
          return { data: { isVip: false, expirationDate: null, daysRemaining: 0 } };
        }),
        api.get('/format/usage').catch(err => {
          console.warn('获取格式化使用情况失败:', err);
          return { data: { todayUsed: 0, dailyLimit: 1, remaining: 1 } };
        }),
        api.get('/ai-chat/usage').catch(err => {
          console.warn('获取AI使用情况失败:', err);
          return { data: { todayCount: 0, limit: 10, remaining: 10 } };
        }),
        api.get('/notes/usage').catch(err => {
          console.warn('获取笔记使用情况失败:', err);
          return { data: { totalCount: 0, limit: 1000, remaining: 1000 } };
        }),
        api.get('/tags').catch(err => {
          console.warn('获取标签失败:', err);
          return { data: [] };
        })
      ]);

      // 提取数据
      const noteStats = noteStatsResponse.data;
      const vipStatus = vipStatusResponse.data;
      const formatUsage = formatUsageResponse.data;
      const aiUsage = aiUsageResponse.data;
      const noteUsage = noteUsageResponse.data;
      const tags = tagsResponse.data;

      // 获取标签数量
      const tagsCount = tags ? tags.length : 0;

      // 更新缓存
      this.cache = {
        noteStats,
        vipStatus,
        aiUsage,
        noteUsage,
        formatUsage,
        tags,
        tagsCount,
        lastUpdated: Date.now()
      };

      this.isInitialized = true;
      console.log(`CacheManager: 初始化完成，耗时: ${Date.now() - startTime}ms`);

      // 发出初始化完成事件
      this.emit('initialized', this.cache);

    } catch (error) {
      console.error('CacheManager: 初始化失败:', error);
      throw error;
    } finally {
      this.isLoading = false;
      this.initPromise = null;
    }
  }

  /**
   * 获取缓存数据
   */
  getData(): Partial<CacheData> {
    return { ...this.cache };
  }

  /**
   * 检查缓存是否有效
   */
  isValid(): boolean {
    if (!this.isInitialized || !this.cache.lastUpdated) {
      return false;
    }
    return Date.now() - this.cache.lastUpdated < this.CACHE_DURATION;
  }

  /**
   * 更新特定类型的缓存数据
   */
  async updateCache(type: keyof CacheData, forceRefresh = false): Promise<void> {
    try {
      console.log(`CacheManager: 更新缓存 - ${type}`);

      let newData: any = null;

      switch (type) {
        case 'noteStats':
          const noteStatsResponse = await api.get('/notes/stats');
          newData = noteStatsResponse.data;
          break;
        case 'vipStatus':
          const vipStatusResponse = await api.get('/vip/status');
          newData = vipStatusResponse.data;
          break;
        case 'aiUsage':
          console.log('🔄 CacheManager: 开始更新AI使用情况...');
          const aiUsageResponse = await api.get('/ai-chat/usage');
          newData = aiUsageResponse.data;
          console.log('🔄 CacheManager: AI使用情况更新完成:', newData);
          break;
        case 'noteUsage':
          const noteUsageResponse = await api.get('/notes/usage');
          newData = noteUsageResponse.data;
          break;
        case 'formatUsage':
          const formatUsageResponse = await api.get('/format/usage');
          newData = formatUsageResponse.data;
          break;
        case 'tags':
          const tagsResponse = await api.get('/tags');
          newData = tagsResponse.data;
          // 同时更新标签数量
          this.cache.tagsCount = newData ? newData.length : 0;
          break;
        case 'tagsCount':
          const tagsCountResponse = await api.get('/tags');
          newData = tagsCountResponse.data ? tagsCountResponse.data.length : 0;
          break;
        default:
          console.warn(`CacheManager: 未知的缓存类型 - ${type}`);
          return;
      }

      // 更新缓存
      this.cache[type] = newData;
      this.cache.lastUpdated = Date.now();

      // 发出更新事件
      console.log(`📢 CacheManager: 发出缓存更新事件 - ${type}`, newData);
      this.emit('updated', type, newData);
      this.emit(`${type}Updated`, newData);

      console.log(`CacheManager: 缓存更新完成 - ${type}`);

    } catch (error) {
      console.error(`CacheManager: 更新缓存失败 - ${type}:`, error);
      throw error;
    }
  }

  /**
   * 强制刷新所有缓存（云端同步）
   */
  async syncFromCloud(): Promise<void> {
    console.log('CacheManager: 开始云端同步...');
    this.isInitialized = false;
    this.cache = {};
    await this.initialize();
    console.log('CacheManager: 云端同步完成');
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    this.cache = {};
    this.isInitialized = false;
    console.log('CacheManager: 缓存已清除');
  }

  /**
   * 获取缓存状态信息
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isLoading: this.isLoading,
      isValid: this.isValid(),
      lastUpdated: this.cache.lastUpdated,
      cacheKeys: Object.keys(this.cache)
    };
  }

  /**
   * 等待缓存初始化完成
   */
  async waitForInitialization(): Promise<void> {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    return new Promise((resolve) => {
      const checkInitialized = () => {
        if (this.isInitialized) {
          resolve();
        } else {
          setTimeout(checkInitialized, 50);
        }
      };
      checkInitialized();
    });
  }
}

// 创建全局单例
const cacheManager = new CacheManager();

export default cacheManager;
export type { CacheData };
